import type { FeedbackDto, FeedbackKind, IssueType } from '@nsb/core';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { api, errorText, queryClient } from '../api.ts';
import { useMe } from '../session.tsx';
import { FeedbackContext, reportsAsStaff, triggerWords, type FeedbackRecord } from './feedback-context.ts';
import { captureScreen } from './feedbackCapture.ts';
import { markFrom, markLines, worthKeeping, type Mark, type MarkTool, type Pt } from './feedbackMarks.ts';
import { PointerArrow } from './drawings.tsx';
import { Button, ErrorLine } from './ui.tsx';

/**
 * One capture tool on every screen, two destinations (spec §10).
 * Screens pin what they show (useFeedbackRecord); rows can open the sheet
 * already pinned to themselves ("Ask about this").
 *
 * The layer sits above everything else in the app (Jason, 2026-10-03: "the
 * bug report layer should be at the highest level of the app so any issues
 * can get reported"): above the drawers, the sticky strips and a page that
 * broke, for anyone signed in. An admin seeing the portal as a client or a
 * sub reports as themselves, with their own choices, and the view is noted.
 */

interface OpenState {
  kind: FeedbackKind | null;
  record: FeedbackRecord | null;
  /** Opened already on a bug or a feature request (the notice at the top does), else asked. */
  issueType: IssueType | null;
  /** undefined while the picture is still being taken; null if it could not be. */
  screenshot: string | null | undefined;
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [pageRecord, setPageRecord] = useState<FeedbackRecord | null>(null);
  const [state, setState] = useState<OpenState | null>(null);
  const [how, showHow] = useState(false);

  const setPage = useCallback((p: string | null, r: FeedbackRecord | null) => {
    setProjectId(p);
    setPageRecord(r);
  }, []);

  // The sheet opens at once; the picture of the screen is taken behind it
  // (the sheet marks itself data-feedback-ui, so it never appears in its own picture).
  const open = useCallback(
    (kind?: FeedbackKind, record?: FeedbackRecord | null, issueType?: IssueType) => {
      showHow(false);
      setState({ kind: kind ?? null, record: record === undefined ? pageRecord : record, issueType: issueType ?? null, screenshot: undefined });
      void captureScreen().then((screenshot) => setState((s) => (s ? { ...s, screenshot } : s)));
    },
    [pageRecord],
  );

  const value = useMemo(() => ({ projectId, pageRecord, setPage, open, how, showHow }), [projectId, pageRecord, setPage, open, how]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackTrigger onClick={() => value.open()} pointed={how} />
      {state ? <FeedbackSheet projectId={projectId} initial={state} onClose={() => setState(null)} /> : null}
    </FeedbackContext.Provider>
  );
}

/**
 * The button at the bottom right of every screen. While how to report is open
 * it is pointed at: it stays above that dialog's backdrop, ringed in bengara,
 * with a word and a drawn arrow to it (Jason, 2026-10-06: "have it point to
 * the feedback icon"); pressing it then opens the sheet as ever.
 */
function FeedbackTrigger({ onClick, pointed }: { onClick: () => void; pointed: boolean }) {
  const { data: me } = useMe();
  if (!me) return null;
  const safe = { marginBottom: 'env(safe-area-inset-bottom)' };
  return (
    <>
      {pointed ? (
        <div data-feedback-ui className="pointer-events-none fixed bottom-[4.25rem] right-4 z-[60] flex flex-col items-end" style={safe} aria-hidden="true">
          <span className="font-display text-[1.125rem] italic leading-none text-ink">This one, on every screen</span>
          <PointerArrow className="mr-6 mt-1" />
        </div>
      ) : null}
      <button
        type="button"
        data-feedback-ui
        onClick={onClick}
        className={`label fixed bottom-4 right-4 z-[60] min-h-11 rounded-[3px] border bg-sheet px-4 shadow-[0_6px_18px_rgba(20,22,19,.08)] ${
          pointed ? 'border-alert !text-ink ring-2 ring-alert ring-offset-2 ring-offset-paper' : 'border-rule-strong !text-ink-2 hover:!text-ink'
        }`}
        style={safe}
      >
        {triggerWords(me)}
      </button>
    </>
  );
}

const TOOLS: { tool: MarkTool; words: string }[] = [
  { tool: 'circle', words: 'Circle' },
  { tool: 'box', words: 'Box' },
  { tool: 'arrow', words: 'Arrow' },
  { tool: 'pen', words: 'Pen' },
];

function FeedbackSheet({ projectId, initial, onClose }: { projectId: string | null; initial: OpenState; onClose: () => void }) {
  const { data: me } = useMe();
  const location = useLocation();
  const staff = reportsAsStaff(me);
  const viewAs = me?.viewAs ?? null;
  const [kind, setKind] = useState<FeedbackKind | null>(initial.kind);
  const [issueType, setIssueType] = useState<IssueType>(initial.issueType ?? 'bug');
  const [body, setBody] = useState('');
  const [includeShot, setIncludeShot] = useState(true);
  const [tool, setTool] = useState<MarkTool>('circle');
  const [marks, setMarks] = useState<Mark[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<FeedbackDto | null>(null);

  const words = staff
    ? { issue: 'Report bug', feature: 'Request feature' }
    : { issue: 'The app isn’t working right', feature: 'Suggest a feature' };
  const heading = !kind ? 'What is it?' : issueType === 'feature' ? words.feature : words.issue;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const send = async () => {
    if (!kind || !body.trim()) return;
    setSending(true);
    setError(null);
    try {
      const screenshot = includeShot && initial.screenshot ? await composite(initial.screenshot, marks) : null;
      const dto = await api<FeedbackDto>('/feedback', {
        body: {
          kind,
          issueType: kind === 'issue' ? issueType : undefined,
          body: body.trim(),
          projectId,
          recordType: initial.record?.type ?? null,
          recordId: initial.record?.id ?? null,
          recordLabel: initial.record?.label ?? null,
          screen: location.pathname,
          context: {
            userAgent: navigator.userAgent.slice(0, 300),
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            build: import.meta.env.VITE_COMMIT_SHA ?? 'dev',
          },
          screenshot,
          annotated: includeShot && marks.length > 0,
        },
      });
      setSent(dto);
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSending(false);
    }
  };

  // Writing it, with a picture of the screen (taken, or being taken): nearly the whole window, the picture large and
  // the tools along its top, the words in a column beside it (under it on a phone) (design study 05, 5A).
  const large = Boolean(kind) && !sent && initial.screenshot !== null;

  const viewing = viewAs && !sent ? (
    <p className="mb-4 border-l-2 border-slate pl-3 text-sm text-ink-2">
      You are seeing the portal as a {viewAs}. What you send goes in as yours{me?.user.name ? `, ${me.user.name.split(/\s+/)[0]}` : ''}, and it is noted as seen from the {viewAs}’s view.
    </p>
  ) : null;

  const writing = (
    <div>
      {initial.record ? (
        <p className="mb-3 text-sm text-ink-2">
          About <span className="text-ink">{initial.record.label}</span>
        </p>
      ) : null}
      <textarea
        autoFocus
        rows={large ? 8 : 4}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={
          issueType === 'feature' ? 'What should it do, and why? The first line becomes the title.' : 'What happened, and what did you expect?'
        }
        className="w-full resize-y rounded-[2px] border border-rule-strong bg-paper p-3 outline-none focus:border-ink"
      />
      {initial.screenshot ? (
        <label className="label mt-3 flex min-h-11 items-center gap-2">
          <input type="checkbox" checked={includeShot} onChange={(e) => setIncludeShot(e.target.checked)} />
          Include the picture of the screen
        </label>
      ) : null}
      <ErrorLine error={error} />
      <div className="mt-4 flex items-center gap-5">
        <Button onClick={send} busy={sending} disabled={!body.trim()}>
          Send
        </Button>
        <Button tone="quiet" onClick={() => setKind(null)}>
          Back
        </Button>
      </div>
    </div>
  );

  if (large) {
    return (
      <div data-feedback-ui className="fixed inset-0 z-[70] bg-ink/45 p-2 sm:p-6" onClick={onClose}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Feedback"
          className="grid h-full w-full grid-rows-[minmax(0,1fr)_auto] overflow-hidden rounded-[6px] bg-sheet shadow-[0_24px_70px_rgba(20,22,19,.18)] lg:grid-cols-[minmax(0,1fr)_22rem] lg:grid-rows-1"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex min-h-0 flex-col border-b border-rule bg-inset lg:border-b-0 lg:border-r">
            <div className="flex flex-wrap items-center gap-2 border-b border-rule bg-sheet px-3 py-2" role="toolbar" aria-label="Mark the picture">
              {TOOLS.map((t) => (
                <button
                  key={t.tool}
                  type="button"
                  aria-pressed={tool === t.tool}
                  disabled={!includeShot || !initial.screenshot}
                  onClick={() => setTool(t.tool)}
                  className={`min-h-9 rounded-[2px] border px-3 text-[0.8125rem] disabled:opacity-40 ${tool === t.tool ? 'border-ink bg-ink text-paper' : 'border-rule-strong bg-paper text-ink-2 hover:text-ink'}`}
                >
                  {t.words}
                </button>
              ))}
              <button
                type="button"
                disabled={!marks.length}
                onClick={() => setMarks((ms) => ms.slice(0, -1))}
                className="min-h-9 rounded-[2px] border border-rule-strong bg-paper px-3 text-[0.8125rem] text-ink-2 hover:text-ink disabled:opacity-40"
              >
                Undo
              </button>
            </div>
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-3">
              {initial.screenshot === undefined ? (
                <p className="label motion-safe:animate-pulse">Taking a picture of the screen</p>
              ) : includeShot && initial.screenshot ? (
                <Annotator src={initial.screenshot} marks={marks} tool={tool} onAdd={(m) => setMarks((ms) => [...ms, m])} />
              ) : (
                <p className="meta">The picture stays out of it.</p>
              )}
            </div>
          </div>
          <div className="min-h-0 overflow-y-auto px-5 pb-6 pt-4">
            <div className="mb-4 flex items-center justify-between">
              <span className="label">{heading}</span>
              <Button tone="quiet" onClick={onClose}>
                Close
              </Button>
            </div>
            {viewing}
            {writing}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-feedback-ui className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/25 sm:items-center" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Feedback"
        className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-[8px] bg-sheet px-5 pb-8 pt-5 shadow-[0_24px_70px_rgba(20,22,19,.18)] sm:rounded-[8px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <span className="label">{sent ? 'Sent' : heading}</span>
          <Button tone="quiet" onClick={onClose}>
            Close
          </Button>
        </div>
        {viewing}
        {sent ? (
          <div>
            <p className="text-lg">
              {staff
                ? `${sent.issueType === 'feature' ? 'Feature request' : 'Bug'} saved, and on its way to GitHub Issues. It is in Settings, under Development tools.`
                : 'Thank you. Jason has it, and you will see its status here.'}
            </p>
            <div className="mt-6">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : !kind ? (
          <div className="divide-y divide-rule border-y border-rule">
            <button
              type="button"
              onClick={() => {
                setIssueType('bug');
                setKind('issue');
              }}
              className="block w-full px-1 py-4 text-left transition-colors duration-[120ms] hover:bg-inset"
            >
              <span className="block text-lg">{words.issue}</span>
              <span className="block text-sm text-ink-3">About the software: private to NSB.</span>
            </button>
            {/* A client suggests features too (Jason, 2026-10-03: "clients should be able to file feature requests"; built 2026-10-06 with the notice at the top). */}
            <button
              type="button"
              onClick={() => {
                setIssueType('feature');
                setKind('issue');
              }}
              className="block w-full px-1 py-4 text-left transition-colors duration-[120ms] hover:bg-inset"
            >
              <span className="block text-lg">{words.feature}</span>
              <span className="block text-sm text-ink-3">{staff ? 'Something the portal should do. Tracked beside the bugs.' : 'Something the portal should do. Private to NSB.'}</span>
            </button>
          </div>
        ) : (
          writing
        )}
      </div>
    </div>
  );
}

/**
 * The picture of the screen, as large as the window allows, drawn on with the
 * tool chosen: a drag makes a circle, a box or an arrow; the pen follows the
 * pointer. Marks are kept in 0..1 of the picture and drawn in its own
 * proportions, so they land where they were drawn at any size.
 */
function Annotator({ src, marks, tool, onAdd }: { src: string; marks: Mark[]; tool: MarkTool; onAdd: (m: Mark) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const start = useRef<Pt | null>(null);
  const [draft, setDraft] = useState<Mark | null>(null);
  const [size, setSize] = useState({ w: 100, h: 100 });
  const point = (e: React.PointerEvent): Pt => {
    const r = box.current!.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const all = draft ? [...marks, draft] : marks;
  return (
    <div
      ref={box}
      className="relative inline-block max-w-full cursor-crosshair touch-none select-none"
      onPointerDown={(e) => {
        (e.target as Element).setPointerCapture?.(e.pointerId);
        const p = point(e);
        start.current = p;
        setDraft(tool === 'pen' ? { kind: 'pen', points: [[p.x, p.y]] } : null);
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        const p = point(e);
        if (tool === 'pen') {
          setDraft((d) => {
            if (d?.kind !== 'pen') return d;
            const [lx, ly] = d.points[d.points.length - 1]!;
            return Math.hypot(p.x - lx, p.y - ly) > 0.002 ? { kind: 'pen', points: [...d.points, [p.x, p.y]] } : d;
          });
        } else {
          setDraft(markFrom(tool, start.current, p));
        }
      }}
      onPointerUp={(e) => {
        if (!start.current) return;
        const m = tool === 'pen' ? draft : markFrom(tool, start.current, point(e));
        start.current = null;
        setDraft(null);
        if (m && worthKeeping(m)) onAdd(m);
      }}
    >
      <img
        src={src}
        alt="The screen as you saw it"
        className="block max-h-[calc(100vh-12rem)] max-w-full select-none lg:max-h-[calc(100vh-9rem)]"
        draggable={false}
        onLoad={(e) => setSize({ w: e.currentTarget.naturalWidth || 100, h: e.currentTarget.naturalHeight || 100 })}
      />
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox={`0 0 ${size.w} ${size.h}`} preserveAspectRatio="none" aria-hidden="true">
        {all.map((m, i) => (
          <path
            key={i}
            d={markLines(m, size.w, size.h)
              .map((line) => line.map(([x, y], j) => `${j ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' '))
              .join(' ')}
            fill="none"
            stroke={BENGARA}
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
    </div>
  );
}

const BENGARA = '#994f42';

/** Burn the marks into the picture that gets sent. */
async function composite(src: string, marks: Mark[]): Promise<string> {
  if (!marks.length) return src;
  const img = new Image();
  img.src = src;
  await img.decode();
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const g = c.getContext('2d')!;
  g.drawImage(img, 0, 0);
  g.strokeStyle = BENGARA;
  g.lineWidth = Math.max(3, c.width / 250);
  g.lineCap = 'round';
  g.lineJoin = 'round';
  for (const m of marks) {
    for (const line of markLines(m, c.width, c.height)) {
      g.beginPath();
      line.forEach(([x, y], j) => (j ? g.lineTo(x, y) : g.moveTo(x, y)));
      g.stroke();
    }
  }
  return c.toDataURL('image/jpeg', 0.85);
}
